import React from 'react';
import {Box, FormHelperText, makeStyles} from '@material-ui/core';
import Warning from '@/Icons/Warning';
import NewWarning from '@/Icons/NewWarning';
import RootStore from '@/Core/mobx/RootStore';

interface WarningMessageProps {
    value: number;
    textStyle?: React.CSSProperties;
    tableCase?: boolean;
    newDesign?: boolean;
}

const useStyles = makeStyles(() => ({
    warningBox: {
        marginTop: '3px',
        display: 'flex',
    },
    warningBoxTable: {
        marginTop: 0,
        marginLeft: '10px',
    },
    warningIcon: {
        display: 'flex',
        alignItems: 'center',
        marginRight: '10px',
    },
}));

export function shouldShowHHIWarning({
    premium,
    annualHHI,
    premiumMode = 'Monthly',
    lowerHouseholdIncomeLimit = false,
}: {
    premium: number;
    annualHHI: number;
    premiumMode?: 'Monthly' | 'Quarterly' | 'SemiAnnually' | 'Annually';
    lowerHouseholdIncomeLimit?: boolean;
}) {
    let monthlyIncome = annualHHI / 12;
    if (premiumMode === 'Quarterly') monthlyIncome = annualHHI / 4;
    if (premiumMode === 'SemiAnnually') monthlyIncome = annualHHI / 2;
    if (premiumMode === 'Annually') monthlyIncome = annualHHI;

    // Use 4% if lowerHouseholdIncomeLimit flag is true, otherwise 5%
    const thresholdPercentage = lowerHouseholdIncomeLimit ? 0.04 : 0.05;
    const threshold = monthlyIncome * thresholdPercentage;
    const exceeds = premium >= threshold;

    // Save HHI warning info for audit purposes (only if we have valid data)
    if (annualHHI > 0 && premium > 0) {
        const warningInfo = {
            thresholdPercentage,
            thresholdAmount: Number(threshold.toFixed(2)),
            wasDisplayed: exceeds,
            calculatedAt: new Date(),
        };

        // Only update if we have access to the parameter store
        if (RootStore.agentPortalStore?.parameterStore) {
            RootStore.agentPortalStore.parameterStore.hhiWarningInfo = warningInfo;
        }
    }

    return {exceeds, threshold: Number(threshold.toFixed(2))};
}

export function WarningMessage({value, tableCase = false, newDesign = false}: WarningMessageProps) {
    const classes = useStyles();

    return (
        <Box className={tableCase ? `${classes.warningBox} ${classes.warningBoxTable}` : classes.warningBox}>
            <Box className={classes.warningIcon}>{newDesign ? <NewWarning /> : <Warning />}</Box>
            <FormHelperText error>
                Based on the client's HHI, ${value.toFixed(2)} would be the typical max monthly premium. Please consult
                the client.
            </FormHelperText>
        </Box>
    );
}
